
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Search, UserPlus, Shield, Users, Trash2, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator' | 'user';
  assigned_at: string;
  profiles: {
    first_name: string;
    last_name: string;
    avatar_url: string;
  } | null;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

const AdminRoles = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState<'admin' | 'moderator'>('moderator');

  const queryClient = useQueryClient();

  const { data: userRoles, isLoading: rolesLoading } = useQuery({
    queryKey: ['admin-user-roles'],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('*')
        .order('assigned_at', { ascending: false });
      
      if (error) throw error;

      // Get profile data for each user role
      const rolesWithProfiles = await Promise.all(
        roles.map(async (role) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', role.user_id)
            .single();
          
          return {
            ...role,
            profiles: profile
          };
        })
      );
      
      return rolesWithProfiles as UserRole[];
    }
  });

  const { data: availableUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-available-users'],
    queryFn: async () => {
      // Get users who are trainers but don't have admin/moderator roles
      const { data: trainers, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('is_trainer', true);
      
      if (error) throw error;

      // Get users with existing roles
      const { data: existingRoles } = await supabase
        .from('user_roles')
        .select('user_id');

      const existingUserIds = new Set(existingRoles?.map(r => r.user_id) || []);
      
      // Filter out users who already have roles
      const availableUsers = trainers?.filter(user => !existingUserIds.has(user.id)) || [];
      
      return availableUsers as User[];
    }
  });

  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'admin' | 'moderator' }) => {
      const { data, error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: role,
          assigned_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-available-users'] });
      setIsAssignDialogOpen(false);
      setSelectedUser('');
      setSelectedRole('moderator');
      toast.success('Role atribuído com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atribuir role: ' + error.message);
    }
  });

  const removeRoleMutation = useMutation({
    mutationFn: async (userRoleId: string) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', userRoleId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-available-users'] });
      toast.success('Role removido com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao remover role: ' + error.message);
    }
  });

  const handleAssignRole = () => {
    if (!selectedUser) {
      toast.error('Por favor selecione um utilizador');
      return;
    }
    
    assignRoleMutation.mutate({
      userId: selectedUser,
      role: selectedRole
    });
  };

  const getRoleBadge = (role: string) => {
    const roleMap = {
      'admin': { label: 'Administrador', variant: 'destructive' as const, icon: Shield },
      'moderator': { label: 'Moderador', variant: 'default' as const, icon: UserPlus },
      'user': { label: 'Utilizador', variant: 'secondary' as const, icon: Users }
    };
    
    const roleInfo = roleMap[role as keyof typeof roleMap] || roleMap.user;
    
    return (
      <div className="flex items-center space-x-2">
        <roleInfo.icon className="h-4 w-4" />
        <Badge variant={roleInfo.variant}>{roleInfo.label}</Badge>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRoles = userRoles?.filter(role => {
    if (!role.profiles) return false;
    const fullName = `${role.profiles.first_name} ${role.profiles.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           role.role.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const adminCount = userRoles?.filter(r => r.role === 'admin').length || 0;
  const moderatorCount = userRoles?.filter(r => r.role === 'moderator').length || 0;

  if (rolesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestão de Administradores</h1>
          <p className="text-gray-600">Gerir roles administrativos da plataforma</p>
        </div>
        
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Atribuir Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Atribuir Role Administrativo</DialogTitle>
              <DialogDescription>
                Selecione um utilizador e o role que pretende atribuir
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div>
                <label className="text-sm font-medium">Utilizador</label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar utilizador" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers?.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select value={selectedRole} onValueChange={(value: 'admin' | 'moderator') => setSelectedRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moderator">Moderador</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAssignRole} disabled={!selectedUser}>
                Atribuir Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userRoles?.length || 0}</div>
            <p className="text-xs text-gray-600">Utilizadores com roles</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Administradores</CardTitle>
            <Shield className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminCount}</div>
            <p className="text-xs text-gray-600">Acesso total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Moderadores</CardTitle>
            <UserPlus className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{moderatorCount}</div>
            <p className="text-xs text-gray-600">Acesso limitado</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Pesquisar por nome ou role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Utilizadores com Roles Administrativos</CardTitle>
          <CardDescription>
            {filteredRoles?.length || 0} utilizadores encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilizador</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Data de Atribuição</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRoles?.map((userRole) => (
                <TableRow key={userRole.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        {userRole.profiles?.first_name?.[0]}{userRole.profiles?.last_name?.[0]}
                      </div>
                      <div>
                        {userRole.profiles?.first_name} {userRole.profiles?.last_name}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getRoleBadge(userRole.role)}
                  </TableCell>
                  <TableCell>
                    {formatDate(userRole.assigned_at)}
                  </TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover Role</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem a certeza que pretende remover o role de{' '}
                            <strong>{userRole.profiles?.first_name} {userRole.profiles?.last_name}</strong>?
                            Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeRoleMutation.mutate(userRole.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Remover
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRoles;
